import { RefObject, useEffect, useState } from 'react';
import { debounce, throttle } from 'underscore';

type TUseTrackResize = {
  ref?: RefObject<HTMLElement | null> | HTMLElement | null;
  trackWindow?: boolean;
  trackType?: 'debounced' | 'throttled' | 'debounced_and_throttled';
  debounceTime?: number;
  throttleTime?: number;
};

const useTrackResize = (props?: TUseTrackResize) => {
  const { ref, trackType = 'fast', debounceTime = 50, throttleTime = 50, trackWindow = true } = props ?? {};
  const initEl = ref instanceof HTMLElement ? ref : ref?.current;
  const [width, setWidth] = useState(trackWindow ? window.innerWidth : (initEl?.offsetWidth ?? 0));
  const [height, setHeight] = useState(trackWindow ? window.innerHeight : (initEl?.offsetHeight ?? 0));

  useEffect(() => {
    const el = ref instanceof HTMLElement ? ref : ref?.current;
    if (!trackWindow && !el) {
      return;
    }

    const handleResize = () => {
      if (trackWindow) {
        setWidth(window.innerWidth);
        setHeight(window.innerHeight);
      }
      if (el) {
        setWidth(el.offsetWidth);
        setHeight(el.offsetHeight);
      }
    };

    // initialize
    handleResize();

    const debounced = debounce(handleResize, debounceTime);
    const throttled = throttle(handleResize, throttleTime);

    const trackDebounced = trackType === 'debounced' || trackType === 'debounced_and_throttled';
    const trackThrottled = trackType === 'throttled' || trackType === 'debounced_and_throttled';
    const isFast = trackType === 'fast';

    if (trackDebounced) {
      if (trackWindow) {
        window.addEventListener('resize', debounced);
      }
      if (el) {
        el.addEventListener('resize', debounced);
      }
    }
    if (trackThrottled) {
      if (trackWindow) {
        window.addEventListener('resize', throttled);
      }
      if (el) {
        el.addEventListener('resize', throttled);
      }
    }

    if (isFast) {
      if (trackWindow) {
        window.addEventListener('resize', handleResize);
      } else if (el) {
        el.addEventListener('resize', handleResize);
      }
    }

    return () => {
      el?.removeEventListener('resize', debounced);
      el?.removeEventListener('resize', throttled);
      el?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', debounced);
      window.removeEventListener('resize', throttled);
      window.removeEventListener('resize', handleResize);
    };
  }, [debounceTime, ref, throttleTime, trackType, trackWindow]);

  return { width, height };
};

export default useTrackResize;
